import { IS_WEB } from '@/shared/constants/utility';
import { CloudinaryResourceType, ReactNativeFile } from '@/shared/types';
import { getMimeType } from '@/shared/utils';
import useConfigStore from '@/stores/useConfigStore';

export interface CloudinaryError extends Error {
  status: number;
  statusText: string;
  responseBody: string;
  url: string;
  imageUrl?: string;
}

interface GenerateSignedUrlResponse {
  timestamp: number;
  signature: string;
  folder: string;
  resource_type: CloudinaryResourceType;
}

interface CloudinaryUploadResponse {
  public_id: string;
  version: number;
  signature: string;
  width: number;
  height: number;
  format: string;
  resource_type: CloudinaryResourceType;
  created_at: string;
  tags: string[];
  bytes: number;
  type: string;
  etag: string;
  placeholder: boolean;
  url: string;
  secure_url: string;
  access_mode: string;
  original_filename: string;
}

class CloudinaryService {
  private static async createCloudinaryError(
    response: Response,
    message: string,
    extraProps?: Record<string, string | number>,
  ): Promise<CloudinaryError> {
    let errorDetails: string;
    try {
      errorDetails = await response.text();
    } catch {
      errorDetails = 'Unable to read error response';
    }

    const error = new Error(message) as CloudinaryError;
    error.status = response.status;
    error.statusText = response.statusText;
    error.responseBody = errorDetails;
    error.url = response.url;

    // Add any extra properties
    if (extraProps) {
      Object.assign(error, extraProps);
    }

    return error;
  }

  static async uploadToCloudinary(
    token: string,
    fileUrl: string,
    resourceType?: CloudinaryResourceType,
  ) {
    const environment = useConfigStore.getState().environment;
    const formData = new FormData();
    formData.append('api_key', environment.cloudinary.apiKey);

    if (IS_WEB) {
      const fileResponse = await fetch(fileUrl);
      if (!fileResponse.ok) {
        throw new Error('Failed to fetch the file from the provided URL');
      }
      const file = await fileResponse.blob();
      formData.append('file', file);
    } else {
      const fileName = fileUrl.split('/').pop() || 'upload.jpg';
      const fileType = getMimeType(fileName, resourceType);

      const fileToUpload: ReactNativeFile = {
        uri: fileUrl,
        name: fileName,
        type: fileType,
      };

      formData.append('file', fileToUpload as unknown as Blob);
    }

    const body = JSON.stringify({
      resource_type: resourceType,
    });

    const response = await fetch(
      `${environment.server.baseUrl}/api/cloudinary/signed-url`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body,
      },
    );

    if (!response.ok) {
      const error = await CloudinaryService.createCloudinaryError(
        response,
        `Failed to get signed URL: ${response.status} ${response.statusText}`,
      );
      throw error;
    }

    const jsonResponse: GenerateSignedUrlResponse = await response.json();

    const { signature, folder, timestamp, resource_type } = jsonResponse;

    formData.append('signature', signature);
    formData.append('folder', folder);
    formData.append('timestamp', timestamp.toString());

    const uploadResponse = await fetch(
      `https://api.cloudinary.com/v1_1/${environment.cloudinary.cloudName}/${resource_type}/upload`,
      {
        method: 'POST',
        body: formData,
      },
    );

    if (!uploadResponse.ok) {
      const error = await CloudinaryService.createCloudinaryError(
        uploadResponse,
        `Cloudinary upload failed: ${uploadResponse.status} ${uploadResponse.statusText}`,
      );
      throw error;
    }

    const result: CloudinaryUploadResponse = await uploadResponse.json();

    return result;
  }

  static async deleteFromCloudinary(token: string, imageUrl: string) {
    const environment = useConfigStore.getState().environment;

    const response = await fetch(
      `${environment.server.baseUrl}/api/cloudinary/delete`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          imageUrl,
        }),
      },
    );

    if (!response.ok) {
      const error = await CloudinaryService.createCloudinaryError(
        response,
        `Failed to delete image from Cloudinary: ${response.status} ${response.statusText}`,
        { imageUrl },
      );
      throw error;
    }
  }
}

export default CloudinaryService;
